import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useState } from "react"
import { CheckCircle } from "lucide-react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { createTicket, selectCreatingTicket } from "@/store/slices/ticketSlice"

export default function NewTicketForm({ onCancel, onSubmit }) {
  const dispatch = useAppDispatch()
  const loading = useAppSelector(selectCreatingTicket)
  const [subject, setSubject] = useState("")
  const [category, setCategory] = useState("")
  const [priority, setPriority] = useState("")
  const [message, setMessage] = useState("")
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!subject || !category || !priority || !message) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      await dispatch(createTicket({
        subject,
        description: message, // backend expects `description`
        category,
        priority
      })).unwrap();

      setIsSubmitted(true);
      setSubject("");
      setCategory("");
      setPriority("");
      setMessage("");
      // Call parent callback to refresh tickets list
      if (onSubmit) onSubmit();
    } catch (err) {
      console.error("Ticket creation failed:", err);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-primary mb-2">
          Ticket Submitted Successfully!
        </h3>
        <p className="text-gray-600 mb-6">
          We've received your request and will get back to you shortly.
        </p>
        <Button
          onClick={() => setIsSubmitted(false)}
          className="bg-primary hover:bg-primary/90 text-white"
        >
          Submit Another Ticket
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label htmlFor="subject">Subject *</Label>
        <Input
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g., Issue with my recent order"
          required
          className="mt-1"
        />
      </div>

      {/* Category & Priority in same row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Category *</Label>
          <Select value={category} onValueChange={setCategory} required>
            <SelectTrigger className="w-full mt-1">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="order">Order Inquiry</SelectItem>
              <SelectItem value="product">Product Question</SelectItem>
              <SelectItem value="shipping">Shipping & Delivery</SelectItem>
              <SelectItem value="billing">Billing & Payments</SelectItem>
              <SelectItem value="account">Account Management</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="priority">Priority *</Label>
          <Select value={priority} onValueChange={setPriority} required>
            <SelectTrigger className="w-full mt-1">
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="High">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Describe your issue in detail..."
          rows={6}
          required
          className="mt-1"
        />
      </div>

      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button
          type="submit"
          className="bg-primary hover:bg-primary/90 text-white"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Ticket"}
        </Button>
      </div>
    </form>
  );
}
